import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Flex,
  Icon,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FaPlay, FaPause } from "react-icons/fa";
import { conversationService } from "../../services/conversationService";
import { getBusinessIdentifier } from "../../utils/auth";

/** Format seconds as m:ss (e.g. 75 -> "1:15"). */
export const formatAudioTime = (seconds: number): string => {
  if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Format seconds as a human-readable duration in minutes/hours.
 * e.g. 300 sec -> "5 min", 5040 sec -> "1hr 24 min".
 */
export const formatAudioDuration = (seconds: number): string => {
  if (isNaN(seconds) || !isFinite(seconds)) return '0 min';
  const totalMins = Math.floor(seconds / 60);
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return hrs > 0 ? `${hrs}hr ${mins} min` : `${mins} min`;
};

/**
 * businessId is the business UUID stored on the logged-in user in localStorage
 * (`user.business_id`, e.g. "8bb2c131-868c-4777-8830-feabe7750b88"). The recording
 * Lambda needs this UUID — NOT the `business_identifier` slug (e.g. "dtc_sojon"),
 * which is only used as a fallback.
 */
const resolveBusinessId = (): string => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.business_id) return user.business_id;
    if (user.businessId) return user.businessId;
  } catch {
    // ignore malformed user JSON and fall through
  }
  return getBusinessIdentifier();
};

interface AudioRecordingPlayerProps {
  conversationId?: string | null;
}

const AudioRecordingPlayer: React.FC<AudioRecordingPlayerProps> = ({ conversationId }) => {
  const toast = useToast();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recordingUrlRef = useRef<string | null>(null); // cache fetched URL
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingRecording, setIsLoadingRecording] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const handlePlayAudio = async () => {
    if (!conversationId) return;

    const businessId = resolveBusinessId();
    if (!businessId) {
      toast({
        title: "Unable to play recording",
        description: "Business ID not found. Please log in again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Toggle pause if already playing.
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    // Fetch (and cache) the recording URL on first play.
    let audioUrl = recordingUrlRef.current;
    if (!audioUrl) {
      setIsLoadingRecording(true);
      try {
        audioUrl = await conversationService.getConversationRecording(businessId, conversationId);
        recordingUrlRef.current = audioUrl;
      } catch (e) {
        setIsLoadingRecording(false);
        toast({
          title: "Recording unavailable",
          description: (e as Error).message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      setIsLoadingRecording(false);
    }

    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });
      audioRef.current.addEventListener('pause', () => setIsPlaying(false));
      audioRef.current.addEventListener('play', () => setIsPlaying(true));
      audioRef.current.addEventListener('loadedmetadata', () => setDuration(audioRef.current?.duration || 0));
      audioRef.current.addEventListener('error', () => {
        setIsPlaying(false);
        recordingUrlRef.current = null; // clear cache on error so a retry refetches
      });
    } else if (audioRef.current.src !== audioUrl) {
      audioRef.current.src = audioUrl;
      audioRef.current.load();
    }

    audioRef.current.play().catch(() => setIsPlaying(false));
  };

  const handleProgressChange = (value: number) => {
    if (audioRef.current) audioRef.current.currentTime = value;
    setCurrentTime(value);
  };

  // Tear down the audio element on unmount.
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Reset everything when the conversation changes.
  useEffect(() => {
    recordingUrlRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [conversationId]);

  // While playing, keep currentTime/duration in sync.
  useEffect(() => {
    if (!isPlaying) return;
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const onLoadedMeta = () => setDuration(audio.duration || 0);
    const onDurationChange = () => setDuration(audio.duration || 0);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMeta);
    audio.addEventListener('durationchange', onDurationChange);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMeta);
      audio.removeEventListener('durationchange', onDurationChange);
    };
  }, [isPlaying]);

  return (
    <Flex align="center" gap={3}>
      <Button
        onClick={handlePlayAudio}
        isDisabled={!conversationId || isLoadingRecording}
        isLoading={isLoadingRecording}
        loadingText="Loading..."
        leftIcon={<Icon as={(isPlaying ? FaPause : FaPlay) as React.ElementType} />}
        variant="outline"
        border={'1px solid grey'}
        h={'30px'}
        w={'150px'}
        borderRadius={0}
        fontSize={'12px'}
      >
        {isPlaying ? 'Pause Audio' : 'Play Audio'}
      </Button>

      {isPlaying && duration > 0 && (
        <Flex align="center" gap={2} minW={'220px'}>
          <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">
            {formatAudioTime(currentTime)}
          </Text>
          <Slider
            aria-label="audio-progress"
            min={0}
            max={duration}
            value={currentTime}
            onChange={handleProgressChange}
            focusThumbOnChange={false}
            flex="1"
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb boxSize={3} />
          </Slider>
          <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">
            {formatAudioTime(duration)}
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

export default AudioRecordingPlayer;
